import { reactive, readonly } from 'vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const FALLBACK_PATHS = Object.freeze({
  terms: '/legal/terms',
  privacy: '/legal/privacy',
  cookies: '/legal/cookies'
})
const MATCHERS = Object.freeze({
  terms: ['terms', 'terms-of-use', 'terms-and-conditions', 'conditions-of-use'],
  privacy: ['privacy', 'privacy-policy', 'privacy-notice', 'data-protection'],
  cookies: ['cookies', 'cookie-policy', 'cookie-notice']
})
const legalPaths = reactive({ ...FALLBACK_PATHS })
let pendingRequest = null

const normalized = (value) => String(value || '')
  .trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
const safeSlug = (value) => {
  const slug = normalized(value)
  return /^[a-z0-9](?:[a-z0-9-]{0,78}[a-z0-9])?$/.test(slug) && !slug.includes('--') ? slug : ''
}
const scorePage = (page, candidates) => candidates.reduce((score, candidate, index) => {
  const slug = normalized(page?.slug)
  const title = normalized(page?.title)
  const priority = candidates.length - index
  if (slug === candidate) score = Math.max(score, 100 + priority)
  if (title === candidate) score = Math.max(score, 80 + priority)
  if (slug.includes(candidate)) score = Math.max(score, 50 + priority)
  if (title.includes(candidate)) score = Math.max(score, 30 + priority)
  return score
}, 0)

export const applyLegalPages = (pages) => {
  const validPages = Array.isArray(pages) ? pages.filter((page) => safeSlug(page?.slug)) : []
  Object.entries(MATCHERS).forEach(([key, candidates]) => {
    const match = validPages
      .map((page) => ({ page, score: scorePage(page, candidates) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)[0]?.page
    const slug = safeSlug(match?.slug)
    legalPaths[key] = slug ? `/legal/${slug}` : FALLBACK_PATHS[key]
  })
  return { ...legalPaths }
}

export const ensureLegalLinks = () => {
  if (pendingRequest) return pendingRequest
  pendingRequest = api.get(EP.pages)
    .then(({ data }) => applyLegalPages(Array.isArray(data) ? data : data?.pages))
    .catch(() => ({ ...legalPaths }))
    .finally(() => { pendingRequest = null })
  return pendingRequest
}

export const useLegalLinks = () => {
  void ensureLegalLinks()
  return readonly(legalPaths)
}

export const getLegalPath = async (key) => {
  if (!Object.hasOwn(FALLBACK_PATHS, key)) return '/'
  await ensureLegalLinks()
  return legalPaths[key] || FALLBACK_PATHS[key]
}
